import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { api, MSG_TYPES, msgTypeLabel, type ApiLink, type ApiSite, type ApiSiteDetail, type ApiSupplier } from '../api';
import { Icon } from '../components/icons';
import { mailboxDraft, MailboxFields, mailboxInput, mailboxMissing } from '../components/MailboxFields';
import { Modal } from '../components/Modal';
import { Btn, Card, DataGate, Dot, PageHead } from '../components/ui';
import { useApp } from '../state';
import { useResource, useFreshSignal } from '../useResource';

// Clients (§4/§5.1) : les SITES (magasins/dépôts) sont provisionnés par le super-admin (feed d'identité par
// GLN, §16 d) — non éditables ici. La console gère les LIENS fournisseur↔site : quels types circulent, la
// config de dossier par client (mode transport per-client) et, vers un fournisseur par e-mail, la BOÎTE du client —
// une par magasin et par fournisseur, chez l'opérateur de son choix.

type Toast = (m: string, t?: 'ok' | 'warn' | 'err' | 'info') => void;

export function Clients() {
  const { showToast, guard } = useApp();
  // Les deux lectures ne forment qu'UNE ressource : la liste des fournisseurs sert à la modale de lien,
  // l'écran n'est donc utilisable qu'avec les deux — les séparer offrirait un demi-écran gréé à moitié.
  const { state, data, reload, error } = useResource(
    async (signal) => {
      const [sites, suppliers] = await Promise.all([
        api.get<ApiSite[]>('/admin/sites', signal),
        api.get<ApiSupplier[]>('/admin/suppliers', signal),
      ]);
      return { sites, suppliers };
    },
    // ⚠️ Vide RÉEL : les sites étant provisionnés ailleurs (super-admin), sans eux la page n'a aucun objet
    // à gérer — et l'état vide dit précisément où ils sont créés.
    (d) => d.sites.length === 0,
  );
  const sites = data?.sites ?? [];
  const suppliers = data?.suppliers ?? [];
  const [selGln, setSelGln] = useState<string | null>(null);
  const [detail, setDetail] = useState<ApiSiteDetail | null>(null);
  const [detailFailed, setDetailFailed] = useState(false); // échec du détail → message INLINE (pas de disparition silencieuse)
  const [linkModal, setLinkModal] = useState<ApiLink | 'new' | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<ApiLink | null>(null); // « Retirer » = action destructive → confirmée

  // Présélection du premier site, une fois la liste connue — un choix DÉJÀ fait par l'opérateur n'est
  // jamais écrasé par un rechargement.
  useEffect(() => { if (data) setSelGln((cur) => cur ?? data.sites[0]?.gln ?? null); }, [data]);

  // ⚠️ Le signal périme le chargement précédent : sans lui, enchaîner deux sites affichait la fiche du
  // premier sous la sélection du second, sans que rien ne le signale (cf. `useFreshSignal`).
  const signalNeuf = useFreshSignal();
  const loadDetail = useCallback(async (gln: string) => {
    const signal = signalNeuf();
    setDetail(null);
    setDetailFailed(false);
    try {
      const record = await api.get<ApiSiteDetail>(`/admin/sites/${gln}`, signal);
      if (signal.aborted) return;
      setDetail(record);
    } catch {
      if (signal.aborted) return; // un autre site a été demandé entre-temps : ce n'est pas un échec
      setDetailFailed(true);
    }
  }, [signalNeuf]);
  useEffect(() => { if (selGln) void loadDetail(selGln); }, [selGln, loadDetail]);

  const removeLink = async (l: ApiLink) => {
    if (!guard() || !selGln) return;
    try {
      const r = await api.del<{ mailboxRemoved?: boolean }>(`/admin/links/${l.id}`);
      showToast(r?.mailboxRemoved ? 'Lien supprimé, avec la boîte e-mail de ce client' : 'Lien supprimé', 'ok');
      setConfirmRemove(null);
      await loadDetail(selGln);
    } catch (e) {
      showToast('Échec : ' + (e as Error).message, 'err');
    }
  };

  const pageHead = (
    <PageHead title="Clients" sub={`${sites.length} site(s) · provisionnés par le super-admin · routage par GLN`} />
  );

  return (
    <DataGate state={state} onRetry={reload} empty={{ title: 'Aucun site raccordé', text: 'Les sites (magasins/dépôts) sont provisionnés par le super-admin (annuaire, par GLN). Une fois provisionnés, gérez ici leurs liens fournisseur.' }} error={error} head={pageHead}>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 320px) minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
        {/* Liste des sites */}
        <Card style={{ overflow: 'hidden' }}>
          {sites.map((c, i) => (
            <button aria-pressed={c.gln === selGln} key={c.gln} type="button" onClick={() => setSelGln(c.gln)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', border: 'none', borderBottom: i < sites.length - 1 ? '1px solid var(--ink-100)' : 'none', background: c.gln === selGln ? 'var(--ink-50)' : 'transparent', boxShadow: c.gln === selGln ? 'inset 2px 0 0 var(--teal-500)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon name={c.type === 'depot' ? 'building' : 'pin'} size={15} style={{ color: 'var(--ink-400)' }} />
                <span style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-800)' }}>{c.name}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-400)' }}>{c.gln}</span>
                <span style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-500)', background: 'var(--ink-100)', padding: '1px 7px', borderRadius: 'var(--r-pill)' }}>{c.type === 'depot' ? 'dépôt' : 'magasin'}</span>
              </div>
            </button>
          ))}
        </Card>

        {/* Détail du site + liens. Échec de chargement = message INLINE avec relance. */}
        {detailFailed && (
          <Card style={{ padding: 16 }}>
            <div style={{ fontSize: 'var(--t-body)', color: 'var(--rose-fg)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="alert" size={15} />Détail du site indisponible (le service n'a pas répondu).
              <Btn size="sm" variant="ghost" mutating={false} icon="refresh" onClick={() => { if (selGln) void loadDetail(selGln); }}>Réessayer</Btn>
            </div>
          </Card>
        )}
        {detail && (
          <Card style={{ overflow: 'hidden' }}>
            <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--ink-100)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink-900)' }}>{detail.site.name}</div>
                <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 3, fontFamily: 'var(--font-mono)' }}>GLN {detail.site.gln}{detail.site.boTenantRef ? ` · tenant ${detail.site.boTenantRef}` : ''}</div>
              </div>
              <Btn size="sm" icon="plus" onClick={() => { if (guard()) setLinkModal('new'); }}>Ajouter un lien</Btn>
            </div>
            <div style={{ padding: 18 }}>
              {/* ⚠️ CASSE NORMALE (relevé) : la maquette écrit « Liens EDI » en 12,5 px, pas en capitales
                  espacées. Les capitales sont réservées aux sur-titres de MÉTADONNÉE (« GLN · TENANT »),
                  pas aux titres de bloc — c'est ce qui les distingue à l'œil. */}
              <div style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-800)', marginBottom: 10 }}>Liens EDI</div>
              {detail.links.length === 0 && <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>Aucun lien — rattachez un fournisseur pour ce site.</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {detail.links.map((l) => {
                  // ⚠️ La pastille dit ce que l'écran SAIT : le fournisseur en pause, la boîte e-mail qui manque. Elle ne
                  //    prétend rien sur la connexion, qui se teste depuis la fiche du fournisseur.
                  const isEmail = l.supplierProtocol === 'email';
                  const state = l.supplierActive === false ? 'off' : isEmail && !l.mailbox ? 'err' : 'ok';
                  return (
                  <div key={l.id} style={{ border: '1px solid var(--ink-100)', borderRadius: 'var(--r-sm)', padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8 }}>
                      <Dot kind={state} />
                      <span style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-800)' }}>{l.supplierName ?? '—'}</span>
                      {l.supplierActive === false && <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>fournisseur en pause</span>}
                      {l.folderName && <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>dossier {l.folderName}{l.inDir ? `/${l.inDir}` : ''}</span>}
                      <span style={{ flex: 1 }} />
                      <Btn size="sm" variant="ghost" icon="cog" onClick={() => { if (guard()) setLinkModal(l); }}>Modifier</Btn>
                      <Btn size="sm" variant="danger" icon="x" onClick={() => { if (guard()) setConfirmRemove(l); }}>Retirer</Btn>
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {l.messageTypes.length === 0 && <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)' }}>aucun type activé</span>}
                      {l.messageTypes.map((t) => <span key={t} style={{ fontSize: 'var(--t-meta)', fontFamily: 'var(--font-mono)', color: 'var(--ink-700)', background: 'var(--ink-100)', padding: '2px 8px', borderRadius: 'var(--r-xs)' }}>{msgTypeLabel(t)}</span>)}
                    </div>
                    {isEmail && (l.mailbox ? (
                      <div style={{ marginTop: 8, fontSize: 'var(--t-meta)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <Icon name="mail" size={13} />
                        <span style={{ fontFamily: 'var(--font-mono)' }}>{l.mailbox.config ? `${l.mailbox.config.user ?? '—'} · ${l.mailbox.config.host ?? '—'}` : 'boîte enregistrée'}</span>
                        {!l.mailbox.active && <span style={{ color: 'var(--ink-400)' }}>· en pause</span>}
                        {l.mailbox.configIssue && <span style={{ color: 'var(--rose-fg)' }}>· {l.mailbox.configIssue.detail}</span>}
                      </div>
                    ) : (
                      <div role="status" style={{ marginTop: 8, fontSize: 'var(--t-meta)', color: 'var(--rose-fg)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <Icon name="alert" size={13} />Aucune boîte : rien n’est relevé ni envoyé pour ce client.
                        <Btn size="sm" variant="ghost" icon="mail" onClick={() => { if (guard()) setLinkModal(l); }}>Déclarer la boîte</Btn>
                      </div>
                    ))}
                  </div>
                  );
                })}
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Confirmation de retrait d'un lien (destructif → jamais en un clic, comme partout ailleurs). */}
      {confirmRemove && detail && (
        <Modal icon="x" tone="err" title="Retirer ce lien ?" sub={`${confirmRemove.supplierName ?? 'Fournisseur'} ↔ ${detail.site.name}`} onClose={() => setConfirmRemove(null)}
          footer={<><Btn variant="ghost" mutating={false} onClick={() => setConfirmRemove(null)}>Annuler</Btn><Btn variant="danger" icon="x" onClick={() => void removeLink(confirmRemove)}>Retirer le lien</Btn></>}>
          <p style={{ margin: 0, fontSize: 'var(--t-lead)', color: 'var(--ink-700)', lineHeight: 1.55 }}>Plus aucun message ne circulera entre ce fournisseur et ce site. Les messages déjà échangés restent dans le journal (rétention §10).{confirmRemove.mailbox ? ' La boîte e-mail de ce client pour ce fournisseur est retirée avec le lien : ses identifiants seront à ressaisir pour le relier de nouveau.' : ''}</p>
        </Modal>
      )}

      {linkModal && detail && (
        <LinkModal
          gln={detail.site.gln}
          suppliers={suppliers}
          existing={linkModal === 'new' ? null : linkModal}
          linkedSupplierIds={detail.links.map((l) => l.supplierId)}
          onClose={() => setLinkModal(null)}
          onDone={async () => { setLinkModal(null); await loadDetail(detail.site.gln); }}
          showToast={showToast}
        />
      )}
    </DataGate>
  );
}

// Modale d'un lien fournisseur↔site : fournisseur + types de messages + config de dossier (mode per-client).
function LinkModal({ gln, suppliers, existing, linkedSupplierIds, onClose, onDone, showToast }: {
  gln: string; suppliers: ApiSupplier[]; existing: ApiLink | null; linkedSupplierIds: string[];
  onClose: () => void; onDone: () => void; showToast: Toast;
}) {
  /** Le préfixe des identifiants qui relient chaque libellé à son champ. */
  const fieldId = useId();
  const { guard } = useApp(); // défense en profondeur : le handler de sauvegarde revérifie la capacité d'écriture
  const available = useMemo(() => (existing ? suppliers : suppliers.filter((s) => !linkedSupplierIds.includes(s.id))), [suppliers, existing, linkedSupplierIds]);
  const [supplierId, setSupplierId] = useState(existing?.supplierId ?? available[0]?.id ?? '');
  const [types, setTypes] = useState<Record<string, boolean>>(Object.fromEntries((existing?.messageTypes ?? []).map((t) => [t, true])));
  const [folderName, setFolderName] = useState(existing?.folderName ?? '');
  const [inDir, setInDir] = useState(existing?.inDir ?? '');
  const [outDir, setOutDir] = useState(existing?.outDir ?? '');
  const [busy, setBusy] = useState(false);
  // Vers un fournisseur par e-mail, le lien porte la boîte de ce client (une par magasin et par fournisseur).
  const supplier = suppliers.find((x) => x.id === supplierId) ?? null;
  const isEmail = supplier?.protocol === 'email';
  const existingMailbox = existing?.mailbox?.config ?? null;
  const [draft, setDraft] = useState(() => mailboxDraft(existingMailbox));

  const inp = { height: 34, borderRadius: 'var(--r-xs)', border: '1px solid var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-800)', fontSize: 'var(--t-lead)', padding: '0 10px', width: '100%' } as const;
  const lbl = { fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink-600)', marginBottom: 4, display: 'block' } as const;

  const save = async () => {
    if (!guard() || !supplierId) return;
    if (isEmail) {
      const miss = mailboxMissing(draft, existingMailbox);
      if (miss.length) { showToast('Boîte du client — champs requis : ' + miss.join(', '), 'warn'); return; }
    }
    setBusy(true);
    const messageTypes = Object.entries(types).filter(([, v]) => v).map(([k]) => k);
    const body = { messageTypes, folderName: folderName.trim() || null, inDir: inDir.trim() || null, outDir: outDir.trim() || null };
    try {
      // ⚠️ À la création, lien et boîte partent ENSEMBLE : le serveur les écrit dans une transaction, ou rien.
      if (!existing) await api.post('/admin/links', { supplierId, gln, ...body, ...(isEmail ? { mailbox: mailboxInput(draft) } : {}) });
      else {
        await api.patch(`/admin/links/${existing.id}`, body);
        if (isEmail) {
          try {
            await api.put(`/admin/links/${existing.id}/mailbox`, { mailbox: mailboxInput(draft) });
          } catch (e) {
            // Le lien est enregistré, la boîte non : on le dit tel quel, et la modale reste ouverte pour corriger.
            showToast('Lien mis à jour, mais la boîte est refusée : ' + (e as Error).message, 'err');
            setBusy(false);
            return;
          }
        }
      }
      showToast(existing ? 'Lien mis à jour' : 'Lien créé', 'ok');
      onDone();
    } catch (e) {
      showToast('Refusé : ' + (e as Error).message, 'err');
      setBusy(false);
    }
  };

  return (
    <Modal wide icon="swap" title={existing ? 'Modifier le lien' : 'Ajouter un lien'} sub={`Site ${gln}`} onClose={onClose}
      footer={<><Btn variant="ghost" mutating={false} onClick={onClose}>Annuler</Btn><Btn onClick={() => void save()} disabled={busy || !supplierId}>{busy ? 'Enregistrement…' : 'Enregistrer'}</Btn></>}>
      <div style={{ display: 'grid', gap: 14 }}>
        <div>
          <label htmlFor={`${fieldId}-0`} style={lbl}>Fournisseur</label>
          <select id={`${fieldId}-0`} value={supplierId} onChange={(e) => setSupplierId(e.target.value)} disabled={!!existing} style={{ ...inp, cursor: existing ? 'default' : 'pointer' }}>
            {available.length === 0 && !existing && <option value="">— tous déjà liés —</option>}
            {(existing ? suppliers : available).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <span id={`${fieldId}-types`} style={lbl}>Types de messages qui circulent</span>
          <div role="group" aria-labelledby={`${fieldId}-types`} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {MSG_TYPES.map((t) => {
              const on = !!types[t.key];
              return (
                <button key={t.key} type="button" onClick={() => setTypes((x) => ({ ...x, [t.key]: !x[t.key] }))}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 11px', border: `1px solid ${on ? 'var(--teal-500)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-sm)', background: on ? 'var(--teal-50)' : 'var(--paper)', cursor: 'pointer', textAlign: 'left' }}>
                  <span style={{ width: 15, height: 15, borderRadius: 'var(--r-r4)', border: `1px solid ${on ? 'var(--teal-500)' : 'var(--ink-300)'}`, background: on ? 'var(--teal-500)' : 'transparent', display: 'grid', placeItems: 'center', color: '#fff' }}>{on && <Icon name="check" size={11} sw={3} />}</span>
                  <span style={{ fontSize: 'var(--t-dense)', fontWeight: 700, color: 'var(--ink-800)', fontFamily: 'var(--font-mono)', minWidth: 130 }}>{t.key}</span>
                  <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        {isEmail ? (
          <div style={{ borderTop: '1px solid var(--ink-100)', paddingTop: 12 }}>
            <span style={{ ...lbl, fontSize: 'var(--t-body)' }}>Boîte e-mail de ce client pour {supplier?.name ?? 'ce fournisseur'}</span>
            <MailboxFields draft={draft} onChange={setDraft} existing={existingMailbox} supplierEmail={{ address: supplier?.emailAddress ?? null, senders: supplier?.emailSenders ?? [] }} />
          </div>
        ) : (
        <div style={{ borderTop: '1px solid var(--ink-100)', paddingTop: 12 }}>
          <span id={`${fieldId}-folder`} style={lbl}>Dossier du client — uniquement si le transport de ce fournisseur est en mode « dossier par client » (§5.1)</span>
          <div role="group" aria-labelledby={`${fieldId}-folder`} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
            <div><label htmlFor={`${fieldId}-1`} style={{ ...lbl, fontSize: 'var(--t-micro)' }}>Nom du dossier</label><input id={`${fieldId}-1`} value={folderName} onChange={(e) => setFolderName(e.target.value)} placeholder="ex. STARTER (alias libre)" style={inp} /></div>
            <div><label htmlFor={`${fieldId}-2`} style={{ ...lbl, fontSize: 'var(--t-micro)' }}>Sous-dossier IN</label><input id={`${fieldId}-2`} value={inDir} onChange={(e) => setInDir(e.target.value)} placeholder="vide = à plat" style={inp} /></div>
            <div><label htmlFor={`${fieldId}-3`} style={{ ...lbl, fontSize: 'var(--t-micro)' }}>Sous-dossier OUT</label><input id={`${fieldId}-3`} value={outDir} onChange={(e) => setOutDir(e.target.value)} placeholder="vide = à plat" style={inp} /></div>
          </div>
        </div>
        )}
      </div>
    </Modal>
  );
}
